<template>
  <div class="promocaoList">
    <div class="topTabs pr-4" ref="tabRef">
      <span
        v-for="({ name, link, activityId, showRedPoint }, i) in topTabs"
        :key="i"
        :class="['tab', tabIndex === i && 'on', showRedPoint && 'withRedPoint']"
        @click="handleTabClick(link!, activityId!, i)"
        >
        {{ name }}
      </span>
    </div>

    <div class="con max-h-[calc(var(--vh)*100-13rem-var(--safe-top))]">
      <template v-if="tabIndex === 0 && !currentComponent">
        <div class="pList">
          <div class="leftBar">
            <div
              v-for="({ name, lists }, index) in promocaoList"
              :key="index"
              :class="['tab', { on: pTabIndex === index, withRedPoint: lists.some((item) => item.showRedPoint)}]"
              @click="showActivityList(index)"
              :title="name"
              >
              {{ name }}
            </div>
          </div>

          <div class="pContent">
            <template v-if="promocaoList[pTabIndex].lists.length">
              <div
                v-for="{
                id,
                showRedPoint,
                link,
                bannerUrl,
                activityId,
                } in promocaoList[pTabIndex].lists"
                                                   :key="id"
                :class="['mb-4 last:mb-0', showRedPoint && 'withRedPoint']"
                @click="gotoActivity({ link, id, activityId }, router)"
                >
                <div class="flex-center">
                  <div class="flex-auto relative item">
                    <Image
                    lazy-load
                        class="w-full rounded-lg overflow-hidden"
                        :src="bannerUrl"
                        >
                        <template v-slot:loading>
                          <van-loading type="spinner" size="20" />
                        </template>
                    </Image>
                  </div>
                </div>
              </div>
            </template>
            <NoData v-else class="w-full" />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="pb-20" v-if="currentComponent">
          <Suspense>
          <component :is="currentComponent" />
          <template #fallback>
            <div class="size-full flex-center">
              <van-loading type="spinner" color="currentColor" />
            </div>
          </template>
          </Suspense>
        </div>
      </template>
    </div>

    <div v-if="!promocaoList.length" class="size-full flex-center">
      <van-loading type="spinner" color="currentColor" class="mt-[30%]" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { gotoActivity } from '@/utils';
  import { useActivityList } from '@/store';
  import type { tActivityType } from '@/types/index';
  import { ACTIVITY_TYPES } from '@/consts';
  import { storeToRefs } from 'pinia';
  import NoData from '@/components/NoData/index.vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

// todo
const tmpActivity = defineAsyncComponent(() => import('@a/views/_Activities/tmp/index.vue'));
const vipActivity = defineAsyncComponent(() => import('@a/views/_Activities/__vip/index.vue'));
const agentcenterActivity = defineAsyncComponent(() => import('@a/views/_Activities/agentcenter/index.vue'));
const giftcodeActivity = defineAsyncComponent(() => import('@a/views/_Activities/__giftcode/index.vue'));

const route = useRoute();
const router = useRouter();
const pTabIndex = ref(Number(localStorage.getItem('pTabIndex')) || 0);
const tabIndex = ref(0);
const tabRef = ref<HTMLDivElement | null>(null);

const compList = [
  {
    type: ACTIVITY_TYPES.AgentReward,
    comp: agentcenterActivity
  },
  {
    type: ACTIVITY_TYPES.VipReward,
    comp: vipActivity
  },
  {
    type: ACTIVITY_TYPES.LevelReward,
    comp: tmpActivity
  },
  {
    type: ACTIVITY_TYPES.AgentReturn,
    comp: tmpActivity
  },
  {
    type: ACTIVITY_TYPES.Remain,
    comp: tmpActivity
  },
  {
    type: ACTIVITY_TYPES.Redeem,
    comp: giftcodeActivity
  },
  {
    type: ACTIVITY_TYPES.PinduoduoReward,
    comp: tmpActivity
  }
];

useActivityList().init()
const { typedList, systemList, activityList } = storeToRefs(useActivityList())

const sortPromocaoList = () => {
  const orderList = [
    [t('Promocao.Deposit'), 'DepositReward'],
    [t('Promocao.Aid'), 'Relief'],
    [t('Promocao.Wheel'), 'MysteryBonus'],
    [t('Promocao.TreasureBox'), 'TreasureBox'],
    [t('Promocao.Rebate'), 'RealtimeRebate'],
    [t('Promocao.RedEnvelopeRain'), 'RedEnvelopeRain'],
  ];

  const lists = [
    { name: t('Promocao.All'), lists: [...activityList.value] },
  ];

  orderList.forEach(([name, type]) => {
    lists.push({ name, lists: typedList.value.find(item => item.type == type)?.configList || [] })
  });

  return lists
};

const showActivityList = (index: number) => {
  pTabIndex.value = index;
  localStorage.setItem('pTabIndex', index + '');
}

const promocaoList = computed(() => sortPromocaoList());

interface iTopTabItem {
  name: string
  activityType?: tActivityType
  link?: string
  id?: string
  activityId?: string
  showRedPoint?: boolean
  linkName?: string
  comp?: any
}

// @ts-ignore
const topTabs: Ref<iTopTabItem[]> = computed(() => {
  return [{
    name: t('home.SideBar.Promocoes'),
    link: '/promocao',
    activityType: 'promocao',
  }, ...systemList.value.map(({activityType, link, id, activityId, showRedPoint, linkName, name}) => ({activityType, link, id, activityId, showRedPoint, linkName, name}))]
  .map(tab => {
    const comp = compList.find(comp => comp.type == tab.activityType);
    if (comp) return {
      ...tab,
      comp: comp.comp
    };
    return tab;
  });
});

const getSubComp = (linkName: string) => {
  return topTabs.value.find(item => item.linkName == linkName)?.comp || null;
};

const currentComponent = computed(() => {
  return route.params.type ? getSubComp(route.params.type as string) : null;
});

const setCurrentTab = (i: number) => {
  tabIndex.value = i;
  nextTick(() => {
    const spans = tabRef.value?.getElementsByTagName('span');
    if (spans?.[i]) {
      spans[i].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  });
};

watch([topTabs, currentComponent], () => {
  if (route.params.type) {
    topTabs.value.forEach((item, i) => {
      if (item.linkName == route.params.type) setCurrentTab(i);
    });
  } else {
    setCurrentTab(0);
  }
}, {immediate: true});

const handleTabClick = async (link: string, activityId: number | string, index: number) => {
  if (index == tabIndex.value) return

  if (index === 0) {
    await router.push('/promocao');
  } else if (link) {
    await router.push(`${link}?activityId=${activityId}`);
  }
  setCurrentTab(index);
}
</script>

<style lang="scss" scoped>
.promocaoList {
  --font-color: var(--theme-primary);
  --bg-color: var(--theme-primary);
  --van-image-placeholder-background: rgba(255, 255, 255, 0.1);

  padding-top: 20px;
  color: white;
  position: relative;

  .topTabs {
    padding-left: 16px;
    height: 34px;
    text-align: center;
    font-size: 15px;
    display: flex;
    margin-bottom: 16px;
    overflow-x: scroll;
    overflow-y: hidden;

    .tab {
      padding: 0 20px;
      line-height: 32px;
      position: relative;

      &.on {
        color: var(--font-color);
        background: linear-gradient(180deg, #2c7952, rgba(44, 121, 82, 0));
        border-radius: 8px 8px 0 0;
      }

      .withRedPoint::after {
        width: 6px;
        height: 6px;
        right: 10px;
        top: 2px;
      }
    }
  }

  .con {
    padding: 0 16px;
    overflow-y: auto;
  }

  .pList {
    .leftBar {
      width: 82px;
      text-align: center;
      position: absolute;

      .tab {
        height: 36px;
        margin-bottom: 12px;
        border: 1px solid #ffffff40;
        border-radius: 5px;
        line-height: 36px;
        font-size: 11px;
        padding: 0 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        position: relative;
        font-weight: bold;

        &.on {
          background-color: var(--bg-color);
          color: #2f1780;
          font-weight: bold;
        }

        .withRedPoint::after {
          width: 6px;
          height: 6px;
          right: 3px;
          top: 3px;
        }
      }
    }

    .pContent {
      width: 250px;
      margin-left: auto;
      padding-bottom: 30px;
      // padding-bottom: 50px;

      .withRedPoint::after {
        width: 12px;
        height: 12px;
        right: 8px;
        top: 8px;
      }

      :deep(.van-image) {
        aspect-ratio: 247 / 97;
        background-color: #00000050;
      }
    }
  }

  .item {
    box-shadow: 0px 4px 4px 0px #00000060;
  }

  // .banner{
  //   border: 5px solid;
  //   border-image-source: linear-gradient(104.46deg, #0C673C 15.74%, #FFD45E 53.43%, #116636 84.26%);
  // }
}
</style>
